import { useRouter } from 'expo-router';
import { Bot } from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import { Platform, ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { FadeIn } from '../../src/components/animations';
import { Box, HStack, Text, VStack } from '../../src/components/primitives';
import { Badge } from '../../src/components/primitives/Badge';
import { useAuth } from '../../src/contexts/AuthContext';
import {
  ActivityFeed,
  AgentConfigCard,
  AgentCustomizationSheet,
  AgentStatusDashboard,
  ExecutionDetailSheet,
  GeneralContextCard,
  UsageSummary,
} from '../../src/features/agents/components';
import {
  useAgentConfig,
  useAgentExecutions,
  useAgentStream,
  useAgentUsage,
  useCancelExecution,
  usePlanUsage,
  useQueueStatus,
  useUpdateAgentConfig,
} from '../../src/features/agents/hooks';
import type { AgentExecution, AgentType } from '../../src/features/agents/types';
import { useOnboardingProfile } from '../../src/features/onboarding/hooks';
import { colors } from '../../src/theme/colors';
import { tokens } from '../../src/theme/tokens';

export default function AIAgentScreen() {
  const { currentOrganization, getAccessToken } = useAuth();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;

  const organizationId = currentOrganization?.id || '';

  const { data: executionsData, isLoading: isLoadingExecutions } = useAgentExecutions({
    organizationId,
    limit: 50,
  });
  const { data: config, isLoading: isLoadingConfig } = useAgentConfig(organizationId);
  const { data: usage, isLoading: isLoadingUsage } = useAgentUsage(organizationId);
  const {
    data: planUsage,
    isLoading: isLoadingPlanUsage,
    isUnavailable: isPlanUsageUnavailable,
  } = usePlanUsage(organizationId);
  const { data: generalContext, isLoading: isLoadingGeneralContext } =
    useOnboardingProfile(organizationId);
  const { data: queueStatus } = useQueueStatus();

  const cancelMutation = useCancelExecution();
  const updateConfigMutation = useUpdateAgentConfig();

  useAgentStream({ organizationId, enabled: !!organizationId, getAccessToken });

  const [selectedExecution, setSelectedExecution] = useState<AgentExecution | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedAgentType, setSelectedAgentType] = useState<AgentType | null>(null);
  const [isCustomizationOpen, setIsCustomizationOpen] = useState(false);

  const handleItemPress = useCallback((execution: AgentExecution) => {
    setSelectedExecution(execution);
    setIsDetailOpen(true);
  }, []);

  const handleDetailClose = useCallback(() => {
    setIsDetailOpen(false);
    setSelectedExecution(null);
  }, []);

  const handleConfigureAgent = useCallback((agentType: AgentType) => {
    setSelectedAgentType(agentType);
    setIsCustomizationOpen(true);
  }, []);

  const handleCustomizationClose = useCallback(() => {
    setIsCustomizationOpen(false);
    setSelectedAgentType(null);
  }, []);

  const handleCancelExecution = useCallback(
    (id: string) => {
      cancelMutation.mutate({ id, organizationId });
      setIsDetailOpen(false);
      setSelectedExecution(null);
    },
    [organizationId, cancelMutation],
  );

  const handleToggleAgent = useCallback(
    (agentType: string, enabled: boolean) => {
      if (!config) return;
      const enabledAgents = enabled
        ? [...config.enabledAgents, agentType]
        : config.enabledAgents.filter((a) => a !== agentType);
      updateConfigMutation.mutate({ organizationId, enabledAgents });
    },
    [config, organizationId, updateConfigMutation],
  );

  const usageBreakdown = useMemo(
    () =>
      usage?.breakdown.map((entry) => ({
        agentType: entry.agentType,
        executions: entry.executionCount,
      })),
    [usage],
  );

  const handleUpgradePress = useCallback(() => {
    router.push({ pathname: '/settings', params: { section: 'billing' } });
  }, [router]);

  const executions = useMemo(() => executionsData?.executions, [executionsData]);

  return (
    <View style={{ flex: 1 }}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <VStack spacing="lg">
          <FadeIn delay={0}>
            <VStack spacing="md">
              <HStack spacing="sm" align="center">
                <Badge
                  variant="subtle"
                  color="purple"
                  size="sm"
                  icon={<Bot size={12} color={colors.accent.purple[400]} />}
                >
                  AI Agents
                </Badge>
              </HStack>
              <VStack spacing="xs">
                <Text variant="h2" color={colors.text.primary}>
                  AI Agent Hub
                </Text>
                <Text variant="body" color={colors.text.secondary}>
                  Monitor and manage your AI-powered sales agents
                </Text>
              </VStack>
            </VStack>
          </FadeIn>

          <FadeIn delay={100}>
            <AgentStatusDashboard
              config={config}
              queueStatus={queueStatus}
              executions={executions}
              isLoading={isLoadingExecutions || isLoadingConfig}
            />
          </FadeIn>

          <Box flexDirection={isDesktop ? 'row' : 'column'} gap="lg">
            <Box style={isDesktop ? styles.mainColumn : styles.mainColumnMobile}>
              <FadeIn delay={200}>
                <ActivityFeed
                  executions={executions}
                  onItemPress={handleItemPress}
                  isLoading={isLoadingExecutions}
                  organizationId={organizationId}
                />
              </FadeIn>
            </Box>

            <Box style={isDesktop ? styles.sideColumn : styles.fullWidth}>
              <VStack spacing="lg">
                <FadeIn delay={300}>
                  <UsageSummary
                    planUsage={planUsage}
                    isPlanUsageUnavailable={isPlanUsageUnavailable}
                    breakdown={usageBreakdown}
                    isLoading={isLoadingUsage || isLoadingPlanUsage}
                    onUpgradePress={handleUpgradePress}
                  />
                </FadeIn>
                <FadeIn delay={400}>
                  <AgentConfigCard
                    config={config}
                    onToggleAgent={handleToggleAgent}
                    onConfigureAgent={handleConfigureAgent}
                    isLoading={isLoadingConfig}
                  />
                </FadeIn>
                <FadeIn delay={500}>
                  <GeneralContextCard
                    profile={generalContext}
                    isLoading={isLoadingGeneralContext}
                  />
                </FadeIn>
              </VStack>
            </Box>
          </Box>
        </VStack>
      </ScrollView>

      <ExecutionDetailSheet
        execution={selectedExecution}
        isOpen={isDetailOpen}
        onClose={handleDetailClose}
        onCancel={handleCancelExecution}
      />

      <AgentCustomizationSheet
        agentType={selectedAgentType}
        organizationId={organizationId}
        config={config}
        isOpen={isCustomizationOpen}
        onClose={handleCustomizationClose}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    // Match the dashboard tab's content inset (lg horizontal + top).
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing.lg,
    paddingBottom: tokens.spacing['4xl'],
  },
  mainColumn: {
    flex: 2,
  },
  sideColumn: {
    flex: 1,
  },
  fullWidth: {
    width: '100%',
  },
  // On iOS/Android the outer `Box gap="lg"` drops between FadeIn-wrapped column
  // children — Yoga collapses the gap when the immediate child is an animated
  // view. Explicit marginBottom on the first column guarantees separation on
  // native; web honors the parent gap.
  mainColumnMobile: {
    width: '100%',
    marginBottom: Platform.OS === 'web' ? 0 : tokens.spacing.lg,
  },
});
